/**
 * Le MODÈLE du questionnaire noté — dépliage, barème, paliers, conseils.
 *
 * Fichier séparé du composant : un `.jsx` qui exporte autre chose qu'un
 * composant casse le Fast Refresh de Vite, et l'écran part blanc sans erreur.
 * Ces fonctions sont pures, donc testables sans monter le moteur.
 */

/** Mélange sans muter la source (Fisher-Yates). */
export const shuffled = (list, rand = Math.random) => {
  const a = [...list]
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export const asArray = (v) => (Array.isArray(v) ? v : [])
export const points = (answer) => {
  const n = Number(answer?.points)
  return Number.isFinite(n) ? n : 0
}

/**
 * Déplie les dimensions en une liste PLATE de questions — c'est elle que le
 * curseur parcourt, le découpage en parties ne servant plus qu'à l'affichage.
 */
export const flattenQuestions = (dimensions, { shuffle = true, rand } = {}) => {
  const flat = []
  asArray(dimensions).forEach((dim, dimIndex) => {
    asArray(dim.questions).forEach((question, qIndex) => {
      const answers = asArray(question.answers)
      flat.push({
        dimIndex,
        qIndex,
        qCount: asArray(dim.questions).length,
        text: question.text ?? '',
        answers: shuffle ? shuffled(answers, rand) : answers,
      })
    })
  })
  return flat
}

/** Le palier atteint : le premier dont la borne supérieure couvre le total. */
export const tierFor = (tiers, total) => {
  const list = asArray(tiers)
  const index = list.findIndex((t) => total <= Number(t.max))
  const resolved = index === -1 ? list.length - 1 : index
  return { tier: list[resolved] ?? null, tierIndex: resolved }
}

/**
 * Le conseil d'une dimension, choisi sur son ratio. Les deux seuils par défaut
 * (34 % et 70 %) découpent en tiers : en dessous « rien n'est en place »,
 * au-dessus « c'est maîtrisé ». Ils se règlent par dimension (`adviceLowBelow`,
 * `adviceHighFrom`) — le barème est du contenu, pas du code.
 */
export const THRESHOLD_LOW = 0.34
export const THRESHOLD_HIGH = 0.7

export const adviceFor = (dimension, ratio) => {
  const low = Number(dimension.adviceLowBelow ?? THRESHOLD_LOW)
  const high = Number(dimension.adviceHighFrom ?? THRESHOLD_HIGH)
  if (ratio < low) return dimension.adviceLow ?? ''
  if (ratio < high) return dimension.adviceMid ?? ''
  return dimension.adviceHigh ?? ''
}

/**
 * Le total atteignable d'une question : la MEILLEURE de ses réponses. Le figer
 * à « 2 points par question » ferait mentir tout barème saisi autrement — un
 * éditeur qui met 3 obtiendrait un ratio supérieur à 1, un « 12/8 » à l'écran
 * et un conseil faux, sans la moindre erreur.
 */
const maxOfQuestion = (question) =>
  asArray(question.answers).reduce((best, answer) => Math.max(best, points(answer)), 0)

/**
 * Le résultat complet, tel qu'il est publié dans le magasin. `answers` est
 * indexé comme la liste plate (une entrée = les points de la question).
 */
export const computeResult = (dimensions, tiers, flat, answers) => {
  const dims = asArray(dimensions).map((dim, dimIndex) => {
    const max = asArray(dim.questions).reduce((sum, q) => sum + maxOfQuestion(q), 0)
    const score = flat.reduce(
      (sum, q, i) => (q.dimIndex === dimIndex ? sum + (answers[i] ?? 0) : sum),
      0,
    )
    const ratio = max > 0 ? score / max : 0
    return {
      key: dim.key ?? String(dimIndex),
      label: dim.label ?? dim.name ?? '',
      name: dim.name ?? dim.label ?? '',
      icon: dim.icon ?? '',
      color: dim.color ?? '',
      score,
      max,
      ratio,
      percent: `${Math.round(ratio * 100)}%`,
      // Le YAML ne CONCATÈNE pas : « 4/10 » se prépare ici, sinon il faudrait
      // trois nœuds et autant de styles pour l'écrire à l'écran.
      scoreLabel: `${score}/${max}`,
      advice: adviceFor(dim, ratio),
    }
  })
  const total = dims.reduce((sum, d) => sum + d.score, 0)
  const max = dims.reduce((sum, d) => sum + d.max, 0)
  const { tier, tierIndex } = tierFor(tiers, total)
  return {
    total,
    max,
    tierIndex,
    tier: tier ? { name: tier.name ?? '', text: tier.text ?? '', watch: tier.watch ?? '' } : { name: '', text: '', watch: '' },
    dimensions: dims,
    scores: Object.fromEntries(dims.map((d) => [d.key, d.score])),
    scoreLabel: `${total}/${max}`,
    // Deux récapitulatifs en TEXTE. `summary` est celui que le formulaire poste
    // et que la notification par e-mail reprend — un score sans son détail
    // n'aide personne. `chartLabel` est l'alternative textuelle du radar : un
    // graphique ne doit jamais porter seul une information.
    chartLabel: dims.map((d) => `${d.label} : ${d.score} sur ${d.max}`).join(', '),
    // Le récapitulatif reprend TOUT l'écran de résultat : score, palier et sa
    // description, point de vigilance, puis chaque dimension avec sa note et son
    // conseil. C'est lui que le formulaire poste et que les notifications
    // reprennent — un e-mail qui promet « le détail de votre diagnostic » et
    // n'enverrait que cinq chiffres ne tiendrait pas sa promesse. Il reste du
    // TEXTE : le corps d'une notification est composé de la saisie d'un visiteur
    // anonyme, que le service de courrier envoie sans échappement HTML.
    summary: [
      `Score global : ${total}/${max}${tier && tier.name ? ` — niveau « ${tier.name} »` : ''}`,
      ...(tier && tier.text ? [tier.text] : []),
      ...(tier && tier.watch ? ['', `Point de vigilance prioritaire : ${tier.watch}`] : []),
      '',
      'Détail par dimension',
      ...dims.flatMap((d) => ['', `${d.label} — ${d.score}/${d.max}`, ...(d.advice ? [d.advice] : [])]),
    ].join('\n'),
  }
}

/** Remplace les jetons `{clé}` d'un libellé par leur valeur. */
export const interpolate = (pattern, vars) =>
  String(pattern ?? '').replace(/\{(\w+)\}/g, (m, key) => (vars[key] !== undefined ? String(vars[key]) : m))
